import { createContext, type ReactNode, useContext, useMemo } from 'react'
import {
  type CurriculumSlice,
  useCurriculumSlice,
} from './curriculum/curriculum-slice'
import type { CommentKind, ReviewType } from './review/contracts'
import { useMockReviewSlice } from './review/mock-review-slice'
import type { ReviewSlice } from './review/review-slice'

/**
 * Composes the review + curriculum slices (spec §11). THE cross-lane
 * interface — after F2 lands, lanes never edit this file again. Extend the
 * `ReviewSlice` / `CurriculumSlice` interfaces additively inside
 * `review/review-slice.tsx` and `curriculum/curriculum-slice.tsx` instead;
 * this file only imports those types, so widened shapes flow through
 * without touching this file.
 */

export type { CommentKind, ReviewType }

export interface KindCounts {
  praise: number
  critique: number
  suggestion: number
}

export interface TypeCounts {
  coding: number
  sd: number
  behavioral: number
  task: number
}

export type { ReviewSlice, CurriculumSlice }

export interface CoachingStoreValue {
  review: ReviewSlice
  curriculum: CurriculumSlice
}

const CoachingStoreContext = createContext<CoachingStoreValue | null>(null)

export function CoachingDashboardMockProvider(props: {
  children: ReactNode
}): ReactNode {
  const review = useMockReviewSlice()
  const curriculum = useCurriculumSlice()

  const value = useMemo<CoachingStoreValue>(
    () => ({ review, curriculum }),
    [review, curriculum],
  )

  return (
    <CoachingStoreContext.Provider value={value}>
      {props.children}
    </CoachingStoreContext.Provider>
  )
}

/** Replaces only the review lane while retaining the parent curriculum lane. */
export function CoachingReviewProvider(props: {
  review: ReviewSlice
  children: ReactNode
}): ReactNode {
  const parent = useCoachingStore()
  const value = useMemo<CoachingStoreValue>(
    () => ({ review: props.review, curriculum: parent.curriculum }),
    [parent.curriculum, props.review],
  )

  return (
    <CoachingStoreContext.Provider value={value}>
      {props.children}
    </CoachingStoreContext.Provider>
  )
}

/** Replaces only the curriculum lane while retaining the parent review lane. */
export function CoachingCurriculumProvider(props: {
  curriculum: CurriculumSlice
  children: ReactNode
}): ReactNode {
  const parent = useCoachingStore()
  const value = useMemo<CoachingStoreValue>(
    () => ({ review: parent.review, curriculum: props.curriculum }),
    [parent.review, props.curriculum],
  )

  return (
    <CoachingStoreContext.Provider value={value}>
      {props.children}
    </CoachingStoreContext.Provider>
  )
}

export function useCoachingStore(): CoachingStoreValue {
  const ctx = useContext(CoachingStoreContext)
  if (!ctx) {
    throw new Error(
      'useCoachingStore must be used within CoachingDashboardMockProvider',
    )
  }
  return ctx
}
